import React from "react";
import cs from "classnames";

import { Button } from "../shared/Button/Button";
import { ProductStoreArtwork } from "./ProductStoreArtwork";
import {
  ProductStoreAction,
  ProductStorePricing,
  ProductStoreProduct,
} from "./ProductStore.types";
import { getProductOffer } from "./productStorePricing";

interface Props {
  product: ProductStoreProduct;
  pricing: ProductStorePricing;
  openingProductType?: string;
  onAction: (action: ProductStoreAction) => void;
}

export const ProductStoreCard = ({
  product,
  pricing,
  openingProductType,
  onAction,
}: Props) => {
  const offer = getProductOffer(pricing, product);
  const { action } = offer;
  const isCheckout = action.type === "checkout";
  const opening = isCheckout && openingProductType === action.productType;

  return (
    <article
      id={product.id}
      className={cs(
        "ProductStoreCard",
        `ProductStoreCard--${product.layout}`,
        `ProductStoreCard--${product.artwork}`
      )}
      tabIndex={-1}
    >
      <div
        className={cs(
          "ProductStoreCard__media",
          `ProductStoreCard__media--${product.artwork}`
        )}
      >
        <ProductStoreArtwork
          artwork={product.artwork}
          alt={product.artworkAlt}
        />
      </div>
      <div className="ProductStoreCard__body">
        <div className="ProductStoreCard__eyebrows">
          <span
            className={cs(
              "ProductStoreCard__eyebrow",
              `ProductStoreCard__eyebrow--${product.eyebrowTone || "teal"}`
            )}
          >
            {product.eyebrow}
          </span>
          {product.secondaryEyebrow && <span>{product.secondaryEyebrow}</span>}
        </div>
        <h3>{product.title}</h3>
        <p>{product.description}</p>
        {product.detail && <p>{product.detail}</p>}
        {product.note && (
          <p className="ProductStoreCard__note">
            {product.note.text}
            {product.note.emphasis && (
              <>
                {" "}
                <strong>{product.note.emphasis}</strong>
              </>
            )}
          </p>
        )}
        <div
          className="ProductStoreCard__price"
          aria-busy={offer.loading || undefined}
        >
          {offer.loading ? (
            <span className="ProductStoreCard__pricePlaceholder" />
          ) : offer.message ? (
            <span className="ProductStoreCard__priceMessage">
              {offer.message}
            </span>
          ) : (
            <>
              <strong>{offer.price}</strong>
              {offer.priceNote && <span>{offer.priceNote}</span>}
            </>
          )}
        </div>
      </div>
      <div className="ProductStoreCard__action">
        {offer.notice ? (
          <p className="ProductStoreCard__notice">{offer.notice}</p>
        ) : (
          <Button
            title={offer.ctaLabel}
            size="small"
            loadSpinner={opening}
            loadTitle="Opening"
            disabled={offer.ctaDisabled || (isCheckout && !!openingProductType)}
            onClick={() => onAction(action)}
          />
        )}
      </div>
    </article>
  );
};
